import { isDimensionName } from "./helpers";
import { DatapointKind, GroupOptions } from "./types";
import { DatapointFormatEnum } from "api/ux/src";
import { InsGridAggs } from "components/widgets/grid/grid-agg-funcs";
import { componentPropsPatcher, WidgetMode } from "store/current-dashboard/types";
const getDatapoint = (element) => {
    if (element) {
        return element.standardDatapoint
            ? { datapoint: element.standardDatapoint, periods: element?.periods, variation: element.format === "variation" }
            : { datapoint: element.customDatapoint, isCustomDatapoint: true, periods: element?.periods, variation: element.format === "variation" };
    }
    return { variation: false };
};
export const getIdDimensionDatapointName = (id) => `Id${id}`;
export const hasWidgetDimensions = (datapointParams, select, optionId) => datapointParams.dimensions
    .some((singleDimension) => singleDimension.name === getIdDimensionDatapointName(optionId)) ||
    select?.flatMap((singleSelect) => Object.values(singleSelect.selectValues))
        .flatMap((singleSelect) => singleSelect.datapointParams)
        .flatMap((singleSelect) => singleSelect?.dimensions)
        .some((singleDimension) => singleDimension?.name === getIdDimensionDatapointName(optionId));
const getOptionPropsFromPatchers = (optionId, patchers) => {
    if (patchers) {
        const pmiPatcher = patchers.find((p) => p.patcher === componentPropsPatcher.setupPublicIndicesContext);
        if (pmiPatcher?.argument?.[optionId]) {
            return { pmiIndex: parseInt(pmiPatcher.argument[optionId], 10) };
        }
    }
    return {};
};
export const mapColDefToColumnOptionState = (col, datapointParams, index, select, onlyMeasures, patchers) => {
    let datapoint = null, kind = DatapointKind.Measure;
    const predicate = (m) => m?.name === col.field || m?.name?.startsWith(`${col.field}}.`);
    if (datapoint === null) {
        datapoint = datapointParams?.measures.find(predicate) ?? null;
    }
    if (datapoint === null) {
        datapoint = select?.flatMap((s) => Object.values(s.selectValues)).flatMap((v) => v.datapointParams)
            ?.flatMap((d) => d?.measures)
            ?.find(predicate) ?? null;
    }
    if (datapoint === null) {
        datapoint = (datapointParams?.dimensions || []).concat(select?.flatMap((s) => Object.values(s.selectValues)).flatMap((v) => v.datapointParams)
            ?.flatMap((d) => d?.dimensions || []))
            ?.find(predicate);
        if (!onlyMeasures) {
            kind = DatapointKind.Dimension;
        }
    }
    if (isDimensionName(datapoint?.standardDatapoint) && hasWidgetDimensions(datapointParams, select, col.field)) {
        kind = DatapointKind.Dimension;
    }
    return {
        ...getDatapoint(datapoint),
        activeAccordionTab: 0,
        columnGroupShow: col.columnGroupShow,
        columnOptionType: col.columnOptionType,
        currentPeriod: false,
        datapointKind: kind,
        enableRowGroup: Boolean(col?.enableRowGroup),
        groupId: null,
        groupOption: col?.groupOption ?? GroupOptions.Expanded,
        headerGroupName: col?.headerGroupName,
        hide: Boolean(col?.hide),
        highchartsDisplayType: col.highchartsDisplayType,
        id: col?.uid,
        isGroup: Boolean(col.children),
        name: col.headerName,
        pinned: col?.pinned ?? false,
        previousPeriod: false,
        relativeValueOption: col?.relativeValueOption,
        rowGroup: Boolean(col?.rowGroup),
        rowGroupIndex: col?.rowGroupIndex,
        sequenceNumber: index,
        sortOrder: col?.sort,
        sortOrderSequence: 0,
        sorted: Boolean(col?.sort),
        tabSectionId: kind.toString(),
        type: col.type?.toString(),
        variation: col.variation,
        width: col.width,
        ...getOptionPropsFromPatchers(col.uid, patchers)
    };
};
const getColDef = (newColumnOption) => {
    const colDef = {
        activeAccordionTab: newColumnOption.activeAccordionTab,
        aggFunc: InsGridAggs.totalData,
        currentPeriod: newColumnOption.currentPeriod,
        enableRowGroup: newColumnOption.enableRowGroup,
        field: newColumnOption.id,
        groupOption: newColumnOption.groupOption,
        headerGroupName: newColumnOption.headerGroupName ?? "",
        headerName: newColumnOption.name ?? "",
        hide: newColumnOption.hide,
        highchartsDisplayType: newColumnOption.highchartsDisplayType,
        pinned: newColumnOption.pinned,
        previousPeriod: newColumnOption.previousPeriod,
        relativeValueOption: newColumnOption.relativeValueOption,
        rowGroup: newColumnOption.rowGroup,
        rowGroupIndex: newColumnOption.rowGroupIndex,
        sequenceNumber: newColumnOption.sequenceNumber,
        sort: newColumnOption.sorted ? newColumnOption.sortOrder : undefined,
        sortIndex: newColumnOption.sortOrderSequence,
        type: newColumnOption.type,
        variation: newColumnOption.variation,
        width: newColumnOption.width
    };
    if (newColumnOption.columnGroupShow) {
        colDef.columnGroupShow = newColumnOption.columnGroupShow;
    }
    return colDef;
};
export const getColDefFromColumnOptionState = (newColumnOption, children = null) => {
    if (newColumnOption.isGroup) {
        const group = {
            activeAccordionTab: newColumnOption.activeAccordionTab,
            aggFunc: InsGridAggs.totalData,
            currentPeriod: newColumnOption.currentPeriod,
            enableRowGroup: newColumnOption.enableRowGroup,
            field: newColumnOption.id,
            groupId: newColumnOption.id,
            groupOption: newColumnOption.groupOption,
            headerGroupName: newColumnOption.headerGroupName ?? "",
            headerName: newColumnOption.name ?? "",
            hide: newColumnOption.hide,
            highchartsDisplayType: newColumnOption.highchartsDisplayType,
            pinned: newColumnOption.pinned,
            previousPeriod: newColumnOption.previousPeriod,
            relativeValueOption: newColumnOption.relativeValueOption,
            rowGroup: newColumnOption.rowGroup,
            rowGroupIndex: newColumnOption.rowGroupIndex,
            sequenceNumber: newColumnOption.sequenceNumber,
            sort: newColumnOption.sorted ? newColumnOption.sortOrder : undefined,
            sortIndex: newColumnOption.sortOrderSequence,
            type: newColumnOption.type,
            variation: newColumnOption.variation,
            width: newColumnOption.width
        };
        if (children) {
            return { ...group, children: children?.map((c) => getColDef(c)) };
        }
        return group;
    }
    return getColDef(newColumnOption);
};
export const getDatapointRequestElementFromColumnOptionState = (newColumnOption) => {
    if (newColumnOption.datapoint) {
        const element = {
            customDatapoint: newColumnOption.isCustomDatapoint ? newColumnOption.datapoint : undefined,
            format: undefined,
            name: newColumnOption.id,
            periods: undefined,
            standardDatapoint: newColumnOption.isCustomDatapoint ? undefined : newColumnOption.datapoint
        };
        if (newColumnOption.datapointKind !== DatapointKind.Dimension && !newColumnOption?.history) {
            element.periods = 1;
        }
        if (newColumnOption.variation) {
            element.format = DatapointFormatEnum.Variation;
        }
        else if (newColumnOption.percent) {
            element.format = DatapointFormatEnum.Percent;
        }
        return element;
    }
    return null;
};
const getConfigs = (widgetComponentProps) => [
    widgetComponentProps,
    ...widgetComponentProps.dataSelectProps?.flatMap((p) => Object.values(p.selectValues)) || []
];
const addCurrentEntityNameToRequest = (widgetComponentProps, entityNameMeasure) => {
    const requestMeasures = (widgetComponentProps.datapointParams?.[0].measures || []);
    if (widgetComponentProps.mode === WidgetMode.Comparison) {
        requestMeasures.push(entityNameMeasure);
    }
    else {
        requestMeasures.splice(requestMeasures.findIndex((m) => m.name === entityNameMeasure.name), 1);
    }
};
export const updateMeasuresAndBuilders = (widgetComponentProps, entityNameMeasure) => {
    for (const config of getConfigs(widgetComponentProps)) {
        const builder = config.pipelineParameter?.compositeObjectBuilder;
        if (widgetComponentProps.mode === WidgetMode.Comparison) {
            builder?.measures?.push(entityNameMeasure.name);
            const measure = config.datapointParams?.flatMap((p) => p.measures || []).find((m) => m.name && builder?.dimensions?.includes(m.name));
            if (measure?.name) {
                builder?.measures?.push(measure.name);
                builder?.dimensions?.splice(builder?.dimensions?.indexOf(measure.name), 1, getIdDimensionDatapointName(measure.name));
            }
        }
        else {
            builder?.measures?.splice(builder?.measures?.indexOf(entityNameMeasure.name), 1);
            const measure = config.datapointParams?.flatMap((p) => p.measures || []).find((m) => m.name && builder?.dimensions?.includes(getIdDimensionDatapointName(m.name)));
            if (measure?.name) {
                builder?.measures?.splice(builder?.measures?.indexOf(measure.name), 1);
                builder?.dimensions?.splice(builder?.dimensions?.indexOf(getIdDimensionDatapointName(measure.name)), 1, measure.name);
            }
        }
    }
    addCurrentEntityNameToRequest(widgetComponentProps, entityNameMeasure);
};
